var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SearchApiService from '../services/SearchApiService';
import { ZdsInputSearch, ZdsPopout } from '@zig-design-system/ui-components-react';
import '../search.scss';
import Results from './Results';
import NoResults from '../../Shared/components/Search/NoResults';
import LoadingSpinner from '../../Shared/components/LoadingSpinner';
import { RootContext } from '../../Context/RootContext';
import { renderToString } from 'react-dom/server';
import Translation from '../../Shared/components/Translation';
const SearchPage = ({ token }) => {
    const [searchWord, setSearchWord] = useState('');
    const [results, setResults] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [usePopout, setUsePopout] = useState(true);
    const inputId = useMemo(() => 'input-search-' + Math.floor(Math.random() * 100000000), []);
    const zdsPopout = useRef();
    const minimumSearchWordLength = 3;
    const extraContentElement = document.getElementById('search--extra-content');
    const languagePath = useContext(RootContext).pageContext.languagePath;
    const ref = useRef(null);
    const searchRef = useRef(null);
    const [pushStateTimeOutId, setPushStateTimeOutId] = useState(null);
    const lastLoggedSearch = useRef(null);
    const logTimeoutId = useRef(null);
    useEffect(() => {
        var _a;
        if ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.closest('*[prevent-search-popout]')) {
            setUsePopout(false);
            if (!searchWord && window.location.search) {
                const urlParams = new URLSearchParams(window.location.search);
                const searchQuery = urlParams.get('search');
                if (searchQuery) {
                    getResults(searchQuery).catch(console.error);
                    const inputField = document.getElementById(inputId);
                    if (inputField) {
                        inputField.setAttribute('value', searchQuery);
                    }
                }
            }
        }
    }, []);
    const logSearch = (searchQuery, resultsCount, hits) => {
        if (logTimeoutId.current) {
            clearTimeout(logTimeoutId.current);
        }
        // Set a new timeout for sending the log with a delay
        logTimeoutId.current = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            const metadata = hits.reduce((acc, hit) => {
                const type = hit._type;
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {});
            const currentSearch = {
                query: searchQuery,
                results: resultsCount,
                metadata: metadata,
            };
            // Only log if different from last logged search
            if (!lastLoggedSearch.current ||
                lastLoggedSearch.current.query !== currentSearch.query ||
                lastLoggedSearch.current.results !== currentSearch.results) {
                try {
                    yield fetch('/?type=1740049151', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(currentSearch),
                    });
                    lastLoggedSearch.current = currentSearch;
                }
                catch (analyticsError) {
                    console.error('Failed to log searchQuery:', analyticsError);
                }
            }
        }), 1000);
    };
    const getResults = (searchQuery) => __awaiter(void 0, void 0, void 0, function* () {
        if (!searchQuery) {
            setResults(null);
            setSearchWord(null);
            window.history.pushState({}, '', window.location.pathname);
            if (usePopout) {
                yield zdsPopout.current.hide();
            }
            return;
        }
        setLoading(true);
        try {
            setSearchWord(searchQuery);
            const searchResponse = yield SearchApiService.search(searchQuery, 'search', languagePath, token);
            const filteredHits = searchResponse.hits.hits.filter((hit) => hit._score > searchResponse.hits.max_score * 0.35);
            logSearch(searchQuery, filteredHits.length, filteredHits);
            filteredHits.length !== 0 ? setResults(filteredHits) : setResults(null);
            if (usePopout) {
                yield zdsPopout.current.show();
            }
        }
        catch (error) {
            // TODO: Logging?
        }
        setLoading(false);
    });
    const onKeywordChange = (event) => {
        const searchQuery = event.currentTarget.value;
        // Clear the previous timeout if it exists
        if (pushStateTimeOutId) {
            clearTimeout(pushStateTimeOutId);
        }
        // Set a new timeout to update the URL after 1.25 seconds + debounce time (total 1.5 seconds)
        const timeoutId = setTimeout(() => {
            window.history.pushState({}, '', `?search=${searchQuery}`);
        }, 1250);
        setPushStateTimeOutId(timeoutId);
        getResults(searchQuery).catch(console.error);
    };
    const renderResults = () => {
        if ((searchWord === null || searchWord === void 0 ? void 0 : searchWord.length) >= minimumSearchWordLength || loading) {
            extraContentElement.classList.add('zds-hidden');
        }
        else {
            extraContentElement.classList.remove('zds-hidden');
        }
        if (results) {
            return React.createElement(Results, { hits: results });
        }
        else if (!loading && (searchWord === null || searchWord === void 0 ? void 0 : searchWord.length) >= minimumSearchWordLength) {
            return React.createElement(NoResults, { className: "zds-padding-top-12" });
        }
        else if (loading) {
            return React.createElement(LoadingSpinner, null);
        }
    };
    return (React.createElement("div", { className: "search-container", ref: ref },
        React.createElement("div", { className: "zds-row" },
            React.createElement("div", { className: "default-content" },
                React.createElement(ZdsInputSearch, { inputAria: { label: 'Zoeken' }, id: inputId, className: "zds-margin-y-1", onZdsSearch: onKeywordChange, debounce: 250, placeholder: renderToString(React.createElement(Translation, { item: "tx_zigsitepackage.search.placeholder", asString: true })), ref: searchRef }),
                usePopout && (React.createElement(ZdsPopout, { target: '#' + inputId, ref: zdsPopout, className: "zds-color-white-foreground zds-color-white-foreground-headline", style: { minHeight: '300px' } }, renderResults())),
                !usePopout && React.createElement("div", { className: "zds-padding-top-16" }, renderResults())))));
};
export default SearchPage;
